import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQuery } from "@/hooks/profile/useProfileQuery";
import { usePostsQuery } from "@/hooks/usePostsQuery";
import { useRoutines } from "@/hooks/useRoutines";
import { useWorkoutPlans } from "@/hooks/useWorkoutPlans";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface RoutineWithDays {
  id: string;
  name: string;
  image_url?: string;
  routine_days?: { day: string }[];
  plan_id?: string;
  plan_mode?: "days" | "sequential";
  plan_position?: number;
  plan_routine_count?: number;
  sequential_index?: number; // 1-based: "Rutina X de N" for sequential plans
}

export const useDashboardData = () => {
  const { data: profile, isLoading: profileLoading } = useProfileQuery();
  const { data: posts, isLoading: postsLoading } = usePostsQuery(profile);
  const { data: routines } = useRoutines();
  const { data: workoutPlans, isLoading: workoutPlansLoading, error: workoutPlansError } = useWorkoutPlans();

  // Query para obtener el gimnasio actual con logo
  const { data: currentGym } = useQuery({
    queryKey: ["current-gym", profile?.gym_id],
    queryFn: async () => {
      if (!profile?.gym_id) return null;
      const { data } = await supabase
        .from("gyms")
        .select("name, logo_url")
        .eq("id", profile.gym_id)
        .single();
      return data;
    },
    enabled: !!profile?.gym_id && !profileLoading,
  });

  console.log("🏢 Current Gym Query State:", {
    gymId: profile?.gym_id,
    profileLoading,
    queryEnabled: !!profile?.gym_id && !profileLoading,
    currentGym: currentGym ? "loaded" : "not loaded"
  });

  console.log("🔍 Dashboard Data Debug:", {
    profile: profile?.id ? "loaded" : "not loaded",
    profileLoading,
    workoutPlansLoading,
    workoutPlansError,
    workoutPlansCount: workoutPlans?.length || 0,
    workoutPlans: workoutPlans?.map(p => ({ id: p.id, name: p.name, isPersonal: p.is_personal }))
  });

  // Solo incluir planes personales o copiados/suscritos (excluir planes públicos originales creados por el profesor)
  const userOwnedPlans = (workoutPlans || []).filter(
    (p: any) => p.is_personal || p.copied_from_plan_id !== null
  );

  // Obtener rutinas de todos los planes del usuario (personales y copiados)
  const { data: allUserRoutines = [], isLoading: routinesLoading, error: routinesError } = useQuery({
    queryKey: ['all-user-routines-with-days', userOwnedPlans.map(p => p.id)],
    queryFn: async () => {
      console.log("🚀 Starting routine fetch - Profile loaded:", !!profile?.id);

      if (userOwnedPlans.length === 0) {
        return [];
      }

      const { data: planRoutines, error } = await supabase
        .from('workout_plan_routines')
        .select(`
          workout_plan_id,
          position,
          routines (
            id,
            name,
            image_url,
            routine_days (
              day
            )
          )
        `)
        .in('workout_plan_id', userOwnedPlans.map(p => p.id))
        .order('position', { ascending: true });

      if (error) {
        console.error("❌ Error fetching workout plan routines:", error);
        throw error;
      }

      // Group by plan to compute counts and indices
      const byPlan = new Map<string, any[]>();
      (planRoutines || []).forEach(pr => {
        if (!pr.routines) return;
        const arr = byPlan.get(pr.workout_plan_id) || [];
        arr.push({ ...pr.routines, plan_position: pr.position ?? 0, plan_id: pr.workout_plan_id });
        byPlan.set(pr.workout_plan_id, arr);
      });

      const planModeMap = new Map(
        userOwnedPlans.map((p: any) => [p.id, (p.mode as string) || 'days'])
      );

      const routines: RoutineWithDays[] = [];
      byPlan.forEach((arr, planId) => {
        const mode = planModeMap.get(planId) as "days" | "sequential";
        const sorted = arr.sort((a, b) => (a.plan_position ?? 0) - (b.plan_position ?? 0));
        sorted.forEach((r, idx) => {
          routines.push({
            ...r,
            plan_mode: mode,
            plan_routine_count: sorted.length,
            sequential_index: idx + 1,
          });
        });
      });

      return routines;
    },
    enabled: userOwnedPlans.length > 0 && !!profile?.id,
    retry: (failureCount) => failureCount < 2,
    staleTime: 5000,
    gcTime: 0,
  });

  console.log("🔄 Routines Query State:", {
    routinesLoading,
    routinesError,
    allUserRoutinesCount: allUserRoutines?.length || 0,
    queryEnabled: userOwnedPlans.length > 0 && !!profile?.id && !workoutPlansLoading
  });

  // Obtener rutinas completadas del día
  const { data: completedRoutines = [] } = useQuery({
    queryKey: ['completed-routines', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      console.log("🔍 Fetching completed routines for user:", profile.id);

      const { data } = await supabase
        .from('routine_progress')
        .select('routine_id, completed_at')
        .eq('user_id', profile.id)
        .gte('completed_at', new Date(new Date().setHours(0, 0, 0, 0)).toISOString());

      console.log("✅ Completed routines fetched:", data?.length || 0, "completed today");
      return data || [];
    },
    enabled: !!profile?.id,
    refetchInterval: false,
    staleTime: 60000,
    gcTime: 300000,
  });

  // Lógica para filtrar rutinas de hoy
  const spanishToEnglishDay = (spanishDay: string): string => {
    const dayMap: Record<string, string> = {
      'lunes': 'monday',
      'martes': 'tuesday',
      'miércoles': 'wednesday',
      'miercoles': 'wednesday',
      'jueves': 'thursday',
      'viernes': 'friday',
      'sábado': 'saturday',
      'sabado': 'saturday',
      'domingo': 'sunday'
    };
    return dayMap[spanishDay.toLowerCase()] || spanishDay;
  };

  const currentSpanishDay = format(new Date(), 'EEEE', { locale: es }).toLowerCase();
  const currentDay = spanishToEnglishDay(currentSpanishDay);

  console.log("📅 Current day (Spanish):", currentSpanishDay);
  console.log("📅 Current day (English):", currentDay);

  // Solo usar rutinas de planes de entrenamiento del usuario (excluyendo rutinas personales sueltas)
  const allRoutines = allUserRoutines || [];
  
  console.log("🔄 User workout plan routines:", allRoutines);
  
  // Helper: completion count per routine (today only)
  const completionCount = (routineId: string) =>
    completedRoutines.filter((cr: any) => cr.routine_id === routineId).length;

  // Split routines by plan mode
  const dayModeRoutines = allRoutines.filter(r => (r as any).plan_mode !== 'sequential');
  const sequentialRoutines = allRoutines.filter(r => (r as any).plan_mode === 'sequential');

  // Day-mode: show routines whose routine_days includes today
  const dayTodayRoutines = dayModeRoutines.filter(routine =>
    routine.routine_days?.some((rd: any) => rd.day === currentDay)
  );

  // Sequential: pick the active routine per plan (lowest position with min completion count)
  const sequentialByPlan = new Map<string, RoutineWithDays[]>();
  sequentialRoutines.forEach(r => {
    const pid = (r as any).plan_id;
    if (!pid) return;
    const arr = sequentialByPlan.get(pid) || [];
    arr.push(r);
    sequentialByPlan.set(pid, arr);
  });

  const sequentialActive: RoutineWithDays[] = [];
  sequentialByPlan.forEach(arr => {
    const sorted = [...arr].sort((a, b) => (a.plan_position ?? 0) - (b.plan_position ?? 0));
    const counts = sorted.map(r => completionCount(r.id));
    const minCount = Math.min(...counts);
    const active = sorted.find(r => completionCount(r.id) === minCount);
    if (active) sequentialActive.push(active);
  });

  const todayRoutines = [...dayTodayRoutines, ...sequentialActive];

  // Has any assigned routines (with days OR sequential)
  const hasAssignedRoutines =
    sequentialRoutines.length > 0 ||
    dayModeRoutines.some(r => r.routine_days && r.routine_days.length > 0);

  console.log("📊 Has assigned routines:", hasAssignedRoutines);

  // Calcular estado de carga general
  const isLoadingRoutines = profileLoading || workoutPlansLoading || routinesLoading;
  
  console.log("🔍 Final Dashboard State:", {
    isLoadingRoutines,
    hasAssignedRoutines,
    todayRoutinesCount: todayRoutines.length,
    allRoutinesCount: allRoutines.length,
    shouldShowEmpty: !isLoadingRoutines && !hasAssignedRoutines,
    shouldShowNoTodayRoutines: !isLoadingRoutines && hasAssignedRoutines && todayRoutines.length === 0
  });

  return {
    profile,
    profileLoading,
    posts,
    postsLoading,
    routines,
    allUserRoutines,
    todayRoutines,
    hasAssignedRoutines,
    completedRoutines,
    isLoadingRoutines,
    currentGym
  };
};